import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

// We would normally import charts from a library like recharts
// For simplicity, we'll mock the data for now

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface AnalyticsData {
  impressions: number;
  clicks: number;
  ctr: number;
  revenue: number;
  dailyStats: Array<{
    date: string;
    impressions: number;
    clicks: number;
    revenue: number;
  }>;
  topPerformingPages: Array<{
    url: string;
    impressions: number;
    clicks: number;
    ctr: number;
  }>;
}

// Mock data
const mockAnalyticsData: AnalyticsData = {
  impressions: 24560,
  clicks: 876,
  ctr: 0.0356,
  revenue: 235.87,
  dailyStats: [
    { date: '2023-05-01', impressions: 3200, clicks: 112, revenue: 31.24 },
    { date: '2023-05-02', impressions: 3350, clicks: 127, revenue: 35.56 },
    { date: '2023-05-03', impressions: 3100, clicks: 105, revenue: 28.47 },
    { date: '2023-05-04', impressions: 3600, clicks: 142, revenue: 39.78 },
    { date: '2023-05-05', impressions: 3800, clicks: 132, revenue: 36.92 },
    { date: '2023-05-06', impressions: 3750, clicks: 129, revenue: 35.67 },
    { date: '2023-05-07', impressions: 3760, clicks: 129, revenue: 28.23 },
  ],
  topPerformingPages: [
    { url: '/blog/ai-trends-2023', impressions: 5230, clicks: 187, ctr: 0.0358 },
    { url: '/blog/machine-learning-basics', impressions: 4150, clicks: 162, ctr: 0.0390 },
    { url: '/tutorials/python-for-beginners', impressions: 3870, clicks: 143, ctr: 0.0369 },
    { url: '/blog/data-science-careers', impressions: 3210, clicks: 118, ctr: 0.0368 },
    { url: '/tutorials/javascript-frameworks', impressions: 2980, clicks: 102, ctr: 0.0342 },
  ]
};

const PublisherAnalytics: React.FC = () => {
  const { user } = useAuth();
  const [timeRange, setTimeRange] = useState<string>('7d');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      
      if (!user?.publisher_id) {
        setError('You need to be logged in as a publisher to view analytics.');
        setLoading(false);
        return;
      }
      
      try {
        // In a real implementation, we would fetch data from the API
        // For demo, we'll use mock data
        setTimeout(() => {
          setAnalyticsData(mockAnalyticsData);
          setLoading(false);
        }, 1000);
        
        // Actual API request would look like:
        /*
        const response = await axios.get(
          `${API_URL}/analytics/publisher/${user.publisher_id}?timeRange=${timeRange}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setAnalyticsData(response.data);
        */
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        setError('Could not load analytics data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchAnalytics();
  }, [user, timeRange]);
  
  const handleTimeRangeChange = (event: any) => {
    setTimeRange(event.target.value);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Publisher Analytics
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small" sx={{ 
              '& .MuiInputLabel-root': { 
                color: 'rgba(255, 255, 255, 0.7)' 
              },
              '& .MuiOutlinedInput-root': { 
                color: 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3B82F6',
                }
              },
              '& .MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}>
              <InputLabel>Time Period</InputLabel>
              <Select
                value={timeRange}
                label="Time Period"
                onChange={handleTimeRangeChange}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#1E293B',
                      color: 'white',
                      '& .MuiMenuItem-root:hover': {
                        bgcolor: 'rgba(59, 130, 246, 0.15)'
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        bgcolor: 'rgba(59, 130, 246, 0.2)'
                      }
                    }
                  }
                }}
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="1y">Last Year</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={() => setTimeRange(timeRange)}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              Refresh Data
            </Button>
          </Grid>
        </Grid>
      </Box>
      
      <Typography variant="h5" gutterBottom>
        Performance Summary
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Impressions
              </Typography>
              <Typography variant="h4">
                {analyticsData?.impressions.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Clicks
              </Typography>
              <Typography variant="h4">
                {analyticsData?.clicks.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Click-Through Rate
              </Typography>
              <Typography variant="h4">
                {analyticsData?.ctr ? `${(analyticsData.ctr * 100).toFixed(2)}%` : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Estimated Revenue
              </Typography>
              <Typography variant="h4">
                ${analyticsData?.revenue.toFixed(2) || '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Daily Performance
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                [Chart showing daily impressions and clicks would be displayed here]
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Chart visualization would typically be implemented using libraries like Recharts or Chart.js
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ad Placement Performance
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                [Pie chart showing performance by placement location would be displayed here]
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Pages
            </Typography>
            <TableContainer>
              <Table aria-label="top performing pages">
                <TableHead>
                  <TableRow>
                    <TableCell>Page URL</TableCell>
                    <TableCell align="right">Impressions</TableCell>
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">CTR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsData?.topPerformingPages.map((page) => (
                    <TableRow key={page.url}>
                      <TableCell component="th" scope="row">
                        {page.url}
                      </TableCell>
                      <TableCell align="right">{page.impressions.toLocaleString()}</TableCell>
                      <TableCell align="right">{page.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{(page.ctr * 100).toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PublisherAnalytics; 